import { useEffect, useState } from "react";
import { Menu, Trash2, Download, Images, ImagePlay, Link2, Link2Off, Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, favoriteImageUrl } from "@/lib/api";
import { PROVIDER_META, findModel } from "@/lib/meta";

export default function GalleryPage({ catalog, onOpenSidebar }) {
  const [items, setItems] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const navigate = useNavigate();

  const shareUrl = (token) => `${window.location.origin}/s/${token}`;

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; } catch (_) { return false; }
  };

  const share = async (f) => {
    try {
      const { data } = await api.post(`/favorites/${f.id}/share`);
      setItems((l) => l.map((x) => (x.id === f.id ? { ...x, share_token: data.share_token } : x)));
      const ok = await copyText(shareUrl(data.share_token));
      setCopiedId(f.id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success(ok ? "Public link copied to clipboard" : "Public link created", { description: shareUrl(data.share_token) });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const unshare = async (f) => {
    try {
      await api.delete(`/favorites/${f.id}/share`);
      setItems((l) => l.map((x) => (x.id === f.id ? { ...x, share_token: null } : x)));
      toast.success("Link revoked — it no longer works");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    api.get("/favorites").then((r) => setItems(r.data)).catch((err) => { toast.error(formatApiError(err)); setItems([]); });
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/favorites/${id}`);
      setItems((l) => l.filter((x) => x.id !== id));
      toast.success("Removed from gallery");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="flex-1 overflow-y-auto" data-testid="gallery-view">
      <header className="sticky top-0 z-10 h-16 flex items-center px-4 lg:px-10 bg-[#090A0F]/70 backdrop-blur-xl border-b border-slate-800/60">
        <button onClick={onOpenSidebar} className="lg:hidden mr-3 text-slate-400 hover:text-white" data-testid="open-sidebar-btn"><Menu className="w-5 h-5" /></button>
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400">Gallery</p>
        {items && <span className="ml-3 text-xs text-slate-500" data-testid="gallery-count">{items.length} pinned</span>}
      </header>

      <div className="max-w-6xl mx-auto px-4 lg:px-10 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-100 rise">Your pinned images</h1>
        <p className="mt-3 text-sm text-slate-400 max-w-xl">Everything you pinned from an image session or the video studio lives here permanently.</p>

        {items && items.length === 0 && (
          <div className="mt-16 text-center py-16 rounded-2xl border border-dashed border-slate-800 rise" data-testid="gallery-empty">
            <Images className="w-8 h-8 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-300 font-medium">Nothing pinned yet</p>
            <p className="text-sm text-slate-500 mt-1">Generate an image, hover it and tap the heart to save it here.</p>
            <Link to="/" className="inline-block mt-6 text-sm text-indigo-300 hover:text-indigo-200" data-testid="gallery-start-link">Start an image inquiry →</Link>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
          {items?.map((f, i) => {
            const meta = PROVIDER_META[f.provider];
            const model = findModel(catalog, f.provider, f.model);
            const url = favoriteImageUrl(f.id);
            return (
              <div key={f.id} style={{ animationDelay: `${i * 40}ms` }} className="rise group rounded-2xl overflow-hidden bg-[#11131E] border border-slate-800/80 hover:border-indigo-500/40 transition-colors" data-testid={`gallery-item-${f.id}`}>
                <div className="relative aspect-square bg-black">
                  {f.kind === "video" ? (
                    <video src={url} controls playsInline preload="metadata" className="w-full h-full object-contain" data-testid={`gallery-video-${f.id}`} />
                  ) : (
                    <img src={url} alt={f.prompt} className="w-full h-full object-cover" loading="lazy" />
                  )}
                  <div className="absolute right-3 top-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {f.kind !== "video" && <button onClick={() => navigate(`/studio?from=${f.id}`)} className="p-2 rounded-lg bg-indigo-500/90 backdrop-blur text-white hover:bg-indigo-600" aria-label="Animate into video" title="Animate into video" data-testid={`gallery-animate-${f.id}`}><ImagePlay className="w-4 h-4" /></button>}
                    <a href={url} download={`omnimodel-${f.id}.${f.kind === "video" ? "mp4" : "png"}`} className="p-2 rounded-lg bg-[#0D0F18]/80 backdrop-blur text-slate-200 hover:text-white" aria-label="Download" data-testid={`gallery-download-${f.id}`}><Download className="w-4 h-4" /></a>
                    <button onClick={() => remove(f.id)} className="p-2 rounded-lg bg-[#0D0F18]/80 backdrop-blur text-slate-200 hover:text-rose-300" aria-label="Remove" data-testid={`gallery-delete-${f.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm text-slate-200 line-clamp-2 leading-snug">{f.prompt || "Untitled"}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <p className="text-[11px] text-slate-500 flex items-center gap-2 min-w-0">
                      <span className={`px-2 py-0.5 rounded-full border ${meta?.badge || "border-slate-700"}`}>{model?.name || (f.model === "sora-2-pro" ? "Sora 2 Pro" : f.model === "sora-2" ? "Sora 2" : f.model)}</span>
                      {new Date(f.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => share(f)} data-testid={`gallery-share-${f.id}`} title={f.share_token ? "Copy public link" : "Create public link"}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${f.share_token ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20" : "bg-[#0D0F18] text-slate-300 border-slate-800 hover:text-white hover:border-slate-600"}`}>
                        {copiedId === f.id ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                        {copiedId === f.id ? "Copied" : f.share_token ? "Copy link" : "Share"}
                      </button>
                      {f.share_token && (
                        <button onClick={() => unshare(f)} data-testid={`gallery-unshare-${f.id}`} title="Revoke public link" className="p-1.5 rounded-lg text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors" aria-label="Revoke link">
                          <Link2Off className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
